import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import api from '../../api/api';
import ItemQuantityField from '../../components/ItemQuantityField';
import { ProductionAllocation, ProductionRoutingLine, validateProductionAllocations } from '../../utils/productionRouting';

export type ProductionRouting = {
  orderId: number;
  version: number;
  locked: boolean;
  isHistorical?: boolean;
  eventDate: string;
  venue: string;
  sourceWarehouseId: number | null;
  lines: ProductionRoutingLine[];
  allocations: ProductionAllocation[];
};

type AllocationDraft = Omit<ProductionAllocation, 'kitchenId' | 'portions'> & {
  key: string;
  kitchenId: number | '';
  portionsText: string;
};

type RoutingOrder = {
  id: number;
  eventDate: string;
  eventVenue?: string | null;
  orderStatusCode?: string | null;
  orderStatusId?: number | null;
};

type Props = {
  order: RoutingOrder;
  routing: ProductionRouting;
  kitchens: Array<{ id: number; name: string }>;
  canEdit: boolean;
  onClose: () => void;
};

const makeAllocationDrafts = (routing: ProductionRouting, nextKey: () => string): AllocationDraft[] => {
  const drafts: AllocationDraft[] = routing.allocations.map(allocation => ({
    ...allocation,
    key: nextKey(),
    portionsText: String(allocation.portions),
  }));
  routing.lines.filter(line => line.portions > 0 && !drafts.some(row => row.kind === line.kind && row.lineId === line.lineId))
    .forEach(line => drafts.push({
      kind: line.kind,
      lineId: line.lineId,
      kitchenId: '',
      portionsText: '',
      key: nextKey(),
    }));
  return drafts;
};

const apiErrorMessage = (error: unknown, fallback: string) =>
  axios.isAxiosError<{ message?: string }>(error) && error.response?.data?.message
    ? error.response.data.message
    : fallback;

export default function ProductionRoutingEditor({ order, routing: initialRouting, kitchens, canEdit, onClose }: Props) {
  const allocationKey = useRef(0);
  const nextAllocationKey = () => `allocation-${++allocationKey.current}`;
  const [routing, setRouting] = useState(initialRouting);
  const [drafts, setDrafts] = useState(() => makeAllocationDrafts(initialRouting, nextAllocationKey));
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [needsReload, setNeedsReload] = useState(false);
  const [wholeOrderKitchenId, setWholeOrderKitchenId] = useState('');
  const requestSequence = useRef(0);

  useEffect(() => () => { requestSequence.current += 1; }, []);

  const reloadRouting = async () => {
    const request = ++requestSequence.current;
    setLoading(true);
    setError('');
    try {
      const response = await api.get<ProductionRouting>(`/eventorders/${order.id}/production-routing`);
      if (request !== requestSequence.current) return;
      setRouting(response.data);
      setDrafts(makeAllocationDrafts(response.data, nextAllocationKey));
      setNeedsReload(false);
      setMessage('Маршрутизацію оновлено. Перевірте дані та повторіть потрібну дію.');
    } catch (requestError: unknown) {
      if (request === requestSequence.current) {
        setError(`${apiErrorMessage(requestError, 'Не вдалося оновити маршрутизацію.')} Повторіть оновлення перед продовженням.`);
      }
    } finally {
      if (request === requestSequence.current) setLoading(false);
    }
  };

  const saveRouting = async (clear = false) => {
    if (saving || loading || needsReload || routing.locked || routing.isHistorical || !canEdit) return;
    if (order.orderStatusCode?.toLowerCase() === 'completed' || order.orderStatusId === 3) {
      setError('Завершене замовлення не можна маршрутизувати.');
      return;
    }
    const allocations: ProductionAllocation[] = clear ? [] : drafts.map(({ key: _key, portionsText, ...draft }) => ({
      ...draft,
      kitchenId: Number(draft.kitchenId),
      portions: Number(portionsText),
    }));
    if (!clear) {
      for (const row of drafts) {
        if (!row.kitchenId || !row.portionsText || !Number.isFinite(Number(row.portionsText)) || Number(row.portionsText) <= 0) {
          setError('Для кожного розподілу оберіть кухню та вкажіть додатну кількість порцій.');
          return;
        }
      }
      const allocationError = validateProductionAllocations(routing.lines, allocations);
      if (allocationError) {
        setError(allocationError);
        return;
      }
    }

    const request = ++requestSequence.current;
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const response = await api.put<ProductionRouting>(`/eventorders/${routing.orderId}/production-routing`, {
        version: routing.version,
        allocations,
      });
      if (request !== requestSequence.current) return;
      setRouting(response.data);
      setDrafts(makeAllocationDrafts(response.data, nextAllocationKey));
      setMessage(clear ? 'Маршрутизацію очищено. Тепер можна зберігати зміни замовлення.' : 'Маршрутизацію кухні збережено.');
    } catch (requestError: unknown) {
      if (request !== requestSequence.current) return;
      setError(apiErrorMessage(requestError, 'Не вдалося зберегти маршрутизацію.'));
      if (axios.isAxiosError(requestError) && requestError.response?.status === 409) {
        setNeedsReload(true);
        setSaving(false);
        setMessage('Маршрутизацію змінено паралельно. Оновлюємо дані; не повторюйте попередній розподіл автоматично.');
        await reloadRouting();
      }
    } finally {
      if (request === requestSequence.current) setSaving(false);
    }
  };

  const completed = order.orderStatusCode?.toLowerCase() === 'completed' || order.orderStatusId === 3;
  const disabled = !canEdit || saving || loading || needsReload || routing.locked || routing.isHistorical || completed;
  const assignWholeOrder = () => {
    const kitchenId = Number(wholeOrderKitchenId);
    if (disabled || !kitchens.some(kitchen => kitchen.id === kitchenId)) return;
    if (drafts.some(row => row.kitchenId !== '') &&
        !window.confirm('Замінити поточний розподіл усіх позицій на вибрану кухню? Зміни буде збережено лише після натискання «Зберегти розподіл».')) return;
    setDrafts(routing.lines.filter(line => line.portions > 0).map(line => ({
      key: nextAllocationKey(),
      kind: line.kind,
      lineId: line.lineId,
      kitchenId,
      portionsText: String(line.portions),
    })));
    setError('');
    setMessage('Усе замовлення призначено вибраній кухні в чернетці. Перевірте та натисніть «Зберегти розподіл».');
  };
  return (
    <div role="presentation" style={{ position: 'fixed', inset: 0, zIndex: 1500, background: 'rgba(0,0,0,.58)', overflowY: 'auto', padding: '18px 12px' }}>
      <section role="dialog" aria-modal="true" aria-label={`Маршрутизація кухонь замовлення ${order.id}`}
        style={{ maxWidth: 900, margin: '16px auto', padding: 18, borderRadius: 'var(--crm-radius-md)', background: 'var(--crm-surface)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)' }}>
        <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <h2 style={{ marginTop: 0 }}>Кухні · замовлення #{order.id}</h2>
          <button type="button" aria-label="Закрити маршрутизацію" disabled={saving || loading} onClick={onClose}>Закрити</button>
        </header>
        <p>Подія: {order.eventDate.slice(0, 10)} · {order.eventVenue || 'Місце не вказано'}</p>
        {loading && <p role="status">Оновлення маршрутизації…</p>}
        {error && <p role="alert" style={{ color: 'var(--crm-danger)' }}>{error}</p>}
        {message && <p role="status" style={{ color: 'var(--crm-success)' }}>{message}</p>}
        {needsReload && <button type="button" disabled={loading || saving} onClick={() => void reloadRouting()}>Повторити оновлення маршрутизації</button>}
        {routing.locked && <p role="note" style={{ padding: 10, background: 'var(--crm-warning-bg)' }}>Маршрутизацію заблоковано після формування плану або дошки кухні.</p>}
        {routing.isHistorical
          ? <p role="note">Історичний маршрут: меню завершеного замовлення виправлено. Нижче показано початкові страви та розподіл, а не поточне меню. Збережені роботи не переплановано; створення нових робіт із цього маршруту недоступне.</p>
          : <p>Статус: {routing.locked || completed ? 'збережений розподіл, лише перегляд' : `${drafts.length ? 'є чернетка розподілу' : 'ще не розподілено'} · збережіть розподіл або очистіть його окремою дією.`}</p>}
        <fieldset disabled={disabled} style={{ margin: '12px 0', minWidth: 0 }}>
          <legend>Усе замовлення на одну кухню</legend>
          <label>
            Виробнича кухня для всього замовлення
            <select value={wholeOrderKitchenId} onChange={event => setWholeOrderKitchenId(event.target.value)}
              style={{ display: 'block', width: '100%', minHeight: 44, fontSize: 16, margin: '8px 0' }}>
              <option value="">Оберіть кухню…</option>
              {kitchens.map(kitchen => <option key={kitchen.id} value={kitchen.id}>{kitchen.name}</option>)}
            </select>
          </label>
          <button type="button" disabled={disabled || !wholeOrderKitchenId || !routing.lines.some(line => line.portions > 0)}
            style={{ minHeight: 44 }} onClick={assignWholeOrder}>Призначити все замовлення</button>
          <p>Повна кількість порцій кожної позиції меню буде призначена вибраній кухні. Після цього можна уточнити окремі позиції та зберегти розподіл.</p>
        </fieldset>
        <div style={{ display: 'grid', gap: 12 }}>
          {routing.lines.map(line => {
            const lineRows = drafts.filter(row => row.kind === line.kind && row.lineId === line.lineId);
            return <article key={`${line.kind}:${line.lineId}`} style={{ padding: 12, border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', background: 'var(--crm-surface-alt)' }}>
              <strong>{line.name}</strong> · {line.portions} порцій
              {line.portions > 0 ? <div style={{ display: 'grid', gap: 12, marginTop: 10 }}>
                {lineRows.map(row => <div key={row.key} style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', gap: 8, alignItems: 'center' }}>
                  <ItemQuantityField
                    items={kitchens.map(kitchen => ({ id: String(kitchen.id), name: kitchen.name }))}
                    value={row.kitchenId === '' ? '' : String(row.kitchenId)}
                    onChange={id => setDrafts(rows => rows.map(item => item.key === row.key ? { ...item, kitchenId: Number(id) } : item))}
                    quantity={row.portionsText}
                    onQuantityChange={value => setDrafts(rows => rows.map(item => item.key === row.key ? { ...item, portionsText: value } : item))}
                    label={`Кухня — ${line.name}`}
                    ariaLabel={`Кухня для ${line.name}`}
                    quantityLabel="Порції"
                    unit="порцій"
                    min={0}
                    step="any"
                    disabled={disabled}
                  />
                  <button type="button" aria-label={`Видалити розподіл ${line.name}`} disabled={disabled}
                    onClick={() => setDrafts(rows => rows.filter(item => item.key !== row.key))}>Видалити</button>
                </div>)}
                <button type="button" disabled={disabled} onClick={() => {
                  const key = nextAllocationKey();
                  setDrafts(rows => [...rows, { key, kind: line.kind, lineId: line.lineId, kitchenId: '', portionsText: '' }]);
                }}>Додати розподіл для {line.name}</button>
                <small>Розподілено: {lineRows.reduce((sum, row) => sum + (Number(row.portionsText) || 0), 0)} / {line.portions} порцій</small>
              </div> : <p>Нульова кількість — маршрутизація не потрібна.</p>}
            </article>;
          })}
          {!routing.lines.length && <p>У замовленні немає меню-рядків для виробництва.</p>}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 16 }}>
          <button type="button" disabled={disabled || !routing.lines.some(line => line.portions > 0)}
            onClick={() => void saveRouting(false)}>{saving ? 'Збереження…' : 'Зберегти розподіл'}</button>
          <button type="button" disabled={disabled || routing.allocations.length === 0}
            onClick={() => void saveRouting(true)}>Очистити маршрутизацію</button>
        </div>
        {!canEdit && <p role="note">Перегляд доступний; для зміни потрібні права редагування <code>page.management.event-orders</code>.</p>}
        {routing.allocations.length > 0 && !routing.locked && <p>Поки є розподіл, повне замовлення дозволяє лише зміни метаданих. Для зміни меню очистіть маршрутизацію, якщо кухонні роботи ще не створені.</p>}
        {routing.locked && <p>Метадані замовлення можна редагувати, але зміни меню потребують перепланування створених кухонних робіт.</p>}
      </section>
    </div>
  );
}
